import {
  type AcademicTerm,
  academicYearTerms,
  calendarEventForAssignment,
  type DailyRecord,
  type HomeworkAssignment,
  type HomeworkRecord,
} from "@classroom/domain";
import {
  createDemoSchoolCalendar,
  demoClasses,
  demoHomeworkTopics,
  demoSchoolDate,
  demoStudents,
  demoWorkspace,
} from "./demo";

function termDate(term: AcademicTerm, index: number, count: number) {
  const start = Date.parse(`${term.startDate}T12:00:00Z`);
  const end = Date.parse(`${term.endDate}T12:00:00Z`);
  return new Date(start + Math.floor(((end - start) * (index + 1)) / (count + 1))).toISOString().slice(0, 10);
}

// A separate scenario keeps the normal Demo fixtures and real workspaces untouched.
export function createSchoolYearDemo(startYear: number) {
  const terms = academicYearTerms(startYear, 3);
  const schoolCalendar = createDemoSchoolCalendar(startYear);
  const workspace = { ...demoWorkspace, academicTerms: terms };
  const teachingDatesByTerm = new Map(
    terms.map((term) => {
      const dates: string[] = [];
      const date = new Date(`${term.startDate}T12:00:00Z`);
      while (date.toISOString().slice(0, 10) <= term.endDate) {
        const value = date.toISOString().slice(0, 10);
        if (demoSchoolDate(value, schoolCalendar) === value) dates.push(value);
        date.setUTCDate(date.getUTCDate() + 1);
      }
      return [term.id, dates] as const;
    }),
  );
  const teachingClasses = demoClasses.filter((item) => item.classRole === "teaching");
  const homeworkAssignments: HomeworkAssignment[] = teachingClasses.flatMap((classroom) =>
    terms.flatMap((term) =>
      Array.from({ length: 11 }, (_, index) => {
        const category = index < 8 ? "homework" : index < 10 ? "assessment" : "dictation";
        const title =
          index < 8
            ? `功課 ${String(index + 1).padStart(2, "0")}：${demoHomeworkTopics[classroom.id]![index]}`
            : index < 10
              ? `單元測驗 ${index - 7}`
              : "核心詞語默書";
        const id = `year-demo-${classroom.id}-${term.id}-${index + 1}`;
        const teachingDates = teachingDatesByTerm.get(term.id)!;
        const dueDate = teachingDates[Math.floor(((teachingDates.length - 1) * (index + 1)) / 12)]!;
        return {
          id,
          workspaceId: workspace.id,
          classId: classroom.id,
          calendarEventId: `event-${id}`,
          category,
          title: `${term.name}｜${title}`,
          maxScore: index < 8 ? [10, 20, 15, 20, 10, 25, 20, 30][index]! : index < 10 ? 100 : 20,
          dueDate,
          note: "全年虛構資料模擬",
          createdAt: `${dueDate}T09:00:00.000Z`,
          updatedAt: `${dueDate}T09:00:00.000Z`,
        };
      }),
    ),
  );
  const homeworkRecords: HomeworkRecord[] = homeworkAssignments.map((assignment, assignmentIndex) => {
    const isExam = assignment.category === "assessment" || assignment.category === "dictation";
    const termIndex = Math.floor((assignmentIndex % 33) / 11);
    return {
      id: `assignment-${assignment.id}`,
      workspaceId: workspace.id,
      classId: assignment.classId,
      assignmentId: assignment.id,
      students: Object.fromEntries(
        demoStudents
          .filter((student) => student.classId === assignment.classId)
          .map((student, index) => {
            const variant = (index + assignmentIndex * 3) % 19;
            // Repeat the fictional profiles when a Demo class has more than twelve students.
            const profileIndex = index % 12;
            const baseline = [34, 43, 52, 61, 72, 82, 90, 57, 65, 76, 48, 87][profileIndex]!;
            const trend = [2, 4, -2, 3, -3, 2, 1, 5, 0, -1, 4, -2][profileIndex]!;
            const percentage = Math.max(0, Math.min(100, baseline + (assignmentIndex % 5) - 2 + termIndex * trend));
            const score = Math.round((assignment.maxScore! * percentage) / 100);
            return [
              student.id,
              assignmentIndex % 33 === 0 && index === 1
                ? { status: "late", score, note: "已補交，需訂正" }
                : assignmentIndex % 33 === 0 && index === 2
                  ? { status: "complete", score: 0, note: "已批改，需訂正" }
                  : variant === 0
                    ? { status: "missing", note: isExam ? "下次上課跟進應考安排" : "下次上課跟進補交" }
                    : variant === 1
                      ? { status: "late", score, note: isExam ? "已補考，需覆核分數" : "已補交，完成訂正" }
                      : variant === 2
                        ? { status: "excused", note: isExam ? "已獲豁免應考" : "已獲豁免" }
                        : { status: "complete", score },
            ];
          }),
      ),
      updatedAt: assignment.updatedAt,
    };
  });
  const dailyRecords: DailyRecord[] = teachingClasses.flatMap((classroom) =>
    terms.flatMap((term, termIndex) =>
      Array.from({ length: 12 }, (_, index) => {
        const date = termDate(term, index, 12);
        return {
          id: `${classroom.id}_${date}`,
          workspaceId: workspace.id,
          classId: classroom.id,
          date,
          students: Object.fromEntries(
            demoStudents
              .filter((student) => student.classId === classroom.id)
              .map((student, studentIndex) => [
                student.id,
                { answers: (studentIndex + index + termIndex) % 4, missingMaterials: (studentIndex + index) % 7 === 0 },
              ]),
          ),
          updatedAt: `${date}T09:00:00.000Z`,
        };
      }),
    ),
  );
  return {
    workspace,
    homeworkAssignments,
    homeworkRecords,
    dailyRecords,
    calendarEvents: [
      ...schoolCalendar,
      ...homeworkAssignments.map((assignment) => calendarEventForAssignment(assignment)),
    ],
  };
}
